import React, {useEffect, useRef} from 'react'
import { useTable, useFilters, usePagination, useResizeColumns, useFlexLayout, useSortBy, useGlobalFilter, useColumnOrder } from 'react-table'
import * as prosConnect from "../prodect/ProsConnect";
import {
    calculateArraySum,
    getActiveProject, getValidInteger,
    MIN_EXPIRE_TIME_MS,
    OBJ_TYPE_ITM,
    OBJ_TYPE_TXT_SOUND, STR_NO_VALUE, TAG_CH_PREFIX,
    NO_DATE_STR, datePickerReturnValueToMillisec, millisecToDatePicker
} from "../prodect/ProsConnect";
import {formatDate, getIconClass, getThisWeekMonday, prosMultiFilterText} from "../prodect/ProsCommonUi"
import DropdownMenu from "../components/DropdownMenu";
import {TimerTextInput} from "../components/TextInput";
import RadioButton from '../components/RadioButton';
import NumPicker from "../components/NumPicker";
import BasicButton from "../components/BasicButton";
import DatePicker from "../components/DatePicker";

export const CH_TAG_INCLUDE = 'include'
export const CH_TAG_EXCLUDE = 'exclude'
export const SAVED_FLT_KEY = "Prodect-filters_"
export const SAVED_COL_WIDTHS = "Prodect-columnWidths_"
export const SAVED_HIDDEN_COLS = "Prodect-hiddenColumns_"
export const SAVED_COL_ORDER = "Prodect-columnOrder_"
export const SAVED_SORT_BY_COL = "Prodect-sortBy_"
export const SAVED_PAGE_INDEX = "Prodect-pageIndex_"
export const SAVED_PAGE_SIZE = "Prodect-pageSize_"

const FLT_RANGE_NONE = 'none'
const FLT_RANGE_MISSING = 'missing'
const FLT_RANGE_MONTH = 'month'
export const FLT_RANGE_WEEK = 'week'
export const FLT_RANGE_DAY = 'day'

const FLT_NONE = 'none'
const FLT_NO_MISSING = 'no_missing'
const FLT_MISSING = 'missing'

export const RESTORE_PROJECT_TABLE_DEFAULTS =
    [SAVED_COL_ORDER, SAVED_COL_WIDTHS, SAVED_FLT_KEY,
        SAVED_HIDDEN_COLS, SAVED_SORT_BY_COL, SAVED_PAGE_SIZE]

export const TABLE_DEFAULT_HIDDEN_COLS = ["ui_title", "ui_obj_id"]

// Cache column widths to reduce fetching from session storage
let widthProjectId
let widthSaved

// Expose filtered OBS id's for location picture summary
var filteredObsIds = []
export var filteredObsChangeCount = 0
export function getFilteredObsIds() {
    return filteredObsIds
}

export function clearFilteredObsIds() {
    filteredObsIds.length = 0
}


let sharePageSize
export function getTablePageSize() { return sharePageSize }


// To get ( for read only ! ) current filtered & sorted rows
let shareRows
export function getReadOnlyTableRows() { return shareRows }


// Shared flag to show table filters
let gloShowTableFilters = false


export function getSavedTableSetting(storage, projectId, key, defaultVal) {
    // 1- use locally stored project table settings
    let savedLocalSettings = storage.getItem(key+projectId)
    if (savedLocalSettings) {
        return JSON.parse(savedLocalSettings)
    }
    // 2 - use project table UI settings
    if (RESTORE_PROJECT_TABLE_DEFAULTS.indexOf(key) >= 0) {
        let tableSettingAttr = key.replace('Prodect-', '').replace('_', '')
        let projectTableUiSetting = prosConnect.getProjectTableUiSetting(tableSettingAttr)
        if (projectTableUiSetting) {
            return projectTableUiSetting
        }
    }
    // 3 - last priority use defaults
    return defaultVal
}

let latestColumnWidths
function rememberLatestColumnWidths(columnWidths) {
    latestColumnWidths = columnWidths
    // console.log('rememberLatestColumnWidths', columnWidths)
}


function migrateFilterRemainingTime(filters) {
    // Backwards compliant for time remaining filter 'ui_timeToDate' migrate filter for new webUI version
    //  - old filter had two values offset in days min, max
    //  - new has three values 0: timeRange (day, week, month) 1: offsetAfter and 2: offsetBefore
    let migratedFilters = filters.map(filter => {
        if (filter.id === "ui_timeToDate") {
            if (filter.value  && filter.value.length === 2) {
                const after = filter.value[0]
                const before = filter.value[1]
                filter.value = [FLT_RANGE_DAY, after, before]
            }
        }
        return filter
    })
    return migratedFilters
}



/**
 * Combine resized column widths with current ones in 'latestColumnWidths' and save to session storage
 * @param columnWidths - key value obj column widths
 */
function exposeActiveColumnWidths(resizedColumnWidths) {
    let activeProjectId = prosConnect.getActiveProjectID()
    for (let key of Object.keys(resizedColumnWidths)) {
        latestColumnWidths[key] = resizedColumnWidths[key]
    }
    let saveWidthJson = JSON.stringify(latestColumnWidths)
    if (saveWidthJson.length > 8) {
        sessionStorage.setItem(SAVED_COL_WIDTHS + activeProjectId, saveWidthJson)
    } else {
        sessionStorage.removeItem(SAVED_COL_WIDTHS + activeProjectId)
    }
}

/**
 * Get table settings - note this needs to be backwards compatible as used
 *      - in runtime when toggle between projects
 *      - when started with new app version parameters saved to local store
 *      - shared link - share settings stored to server and fetched by a link key
 * @param projectId - typically active project
 * @param who - name of caller - can be used for debugging
 * @param getColumnOrder - restore column order only when needed by a caller
 * @returns {{
 *          hiddenColumns: (any|string[]),
 *          columnOrder: (any|*[]),
 *          pageIndex: number, pageSize: number,
 *          sortBy: (any|*[]),
 *          columnResizing: (any|{columnWidths: {}}),
 *          filters: (any|*[])}}
 */
export function restoreTableSettings(projectId, who, getColumnOrder) {
    let uiSettings = {
        columnWidths: getSavedTableSetting(sessionStorage, projectId, SAVED_COL_WIDTHS, {}),
        hiddenColumns: getSavedTableSetting(localStorage, projectId, SAVED_HIDDEN_COLS, TABLE_DEFAULT_HIDDEN_COLS),
        filters: getSavedTableSetting(sessionStorage, projectId, SAVED_FLT_KEY, []),
        sortBy: getSavedTableSetting(sessionStorage, projectId, SAVED_SORT_BY_COL, []),
        pageIndex: getSavedTableSetting(sessionStorage, projectId, SAVED_PAGE_INDEX, 0),
        pageSize: getSavedTableSetting(sessionStorage, projectId, SAVED_PAGE_SIZE, 20),
    }
    if (getColumnOrder) {
        uiSettings.columnOrder = getSavedTableSetting(localStorage, projectId, SAVED_COL_ORDER, [])
    }
    uiSettings.filters = migrateFilterRemainingTime(uiSettings.filters)
    rememberLatestColumnWidths(uiSettings.columnWidths)
    return uiSettings
}


let tableSettingsChangedCount = 0
let tableSettingsChangedCountPrev = -1
export function hasTableSettingsChanged() {
    let wasChanged = tableSettingsChangedCountPrev !== tableSettingsChangedCount
    tableSettingsChangedCountPrev = tableSettingsChangedCount
    return wasChanged
}

export function setTableSettings(projectId, uiSettings) {
    // Override project's local table settings from project link settings stored to server
    function saveSetting(storage, key, fieldName) {
        if (uiSettings.hasOwnProperty(fieldName)) {
            storage.setItem(key+projectId, JSON.stringify(uiSettings[fieldName]))
        } else {
            storage.removeItem(key+projectId)
        }
    }
    // prosLogX("setTableSettings columnWidths:", uiSettings.columnWidths)
    clearColumnWidthCache()
    saveSetting(sessionStorage, SAVED_COL_WIDTHS, 'columnWidths')
    saveSetting(localStorage, SAVED_HIDDEN_COLS, 'hiddenColumns')
    saveSetting(localStorage, SAVED_COL_ORDER, 'columnOrder')
    saveSetting(sessionStorage, SAVED_FLT_KEY, 'filters')
    saveSetting(sessionStorage, SAVED_SORT_BY_COL, 'sortBy')
    saveSetting(sessionStorage, SAVED_PAGE_INDEX, 0, 'pageIndex')
    saveSetting(sessionStorage, SAVED_PAGE_SIZE, 'pageSize')
    tableSettingsChangedCount++
}

export function setTableFilter(projectId, filters) {
    sessionStorage.setItem(SAVED_FLT_KEY + projectId, JSON.stringify(filters))
}

export function setTableSort(projectId, sortBy) {
    sessionStorage.setItem(SAVED_SORT_BY_COL + projectId, JSON.stringify(sortBy))
}

export function getTableFilter(projectId) {
    return getSavedTableSetting(sessionStorage, projectId, SAVED_FLT_KEY, [])
}

export function getTableSort(projectId) {
    return getSavedTableSetting(sessionStorage, projectId, SAVED_SORT_BY_COL, [])
}

export function dumpTableFilter(projectId, title) {
    // let flt = getSavedTableSetting(sessionStorage, projectId, SAVED_FLT_KEY, [])
    // prosLogX(title, "dumpTableFilter projectId:", projectId, flt)
}

/**
 * Return table filter based on active project settings which states can be late
 * @returns {*[]|{id}}    - state TAG filter for state column "ongoing"
 */
export function getTableFilterStateOngoing() {
    let activeProject = prosConnect.getActiveProject()
    if (activeProject) {
        let stateTag = activeProject.getStateRbTag()
        let stateTagInd = activeProject.tagIdToIndex(stateTag)
        if (stateTagInd !== undefined) {
            let mvTag = activeProject.getMvTagSettings(stateTagInd)
            if (mvTag) {
                let statesOnGoing = mvTag.options
                    .filter(option => option.inUse && option.isLate)
                    .map(option => option.label)
                if (statesOnGoing.length > 0) {
                    return [{
                        "id": stateTag,
                        "value": statesOnGoing
                    }]
                    // example [{ "id": "tag2", "value": ["Noticed", "Can't fix", "Other"]}]
                }
            }
        }
    }
    return []
}

export function clearColumnWidthCache() {
    widthProjectId = undefined
}

export const PROS_TBL_DEFAULT_WIDTH = 150
export function getColumnWidth(projectId, columnId, defaultWidth=PROS_TBL_DEFAULT_WIDTH) {
    if (widthProjectId !== projectId) {
        let savedWidthJson = sessionStorage.getItem(SAVED_COL_WIDTHS + projectId)
        widthSaved = savedWidthJson ? JSON.parse(savedWidthJson) : undefined
        widthProjectId = projectId
    }
    // if (columnId === 'created_by') {
    //     let w = widthSaved && widthSaved.hasOwnProperty(columnId) ? widthSaved[columnId] : defaultWidth
    //     console.log("getColumnWidth()", columnId, w, projectId)
    // }
    return widthSaved && widthSaved.hasOwnProperty(columnId) ? widthSaved[columnId] : defaultWidth
}

export function clearToDefaultTableSettings() {
    sessionStorage.removeItem(SAVED_COL_WIDTHS + getActiveProject().getPid())
    localStorage.removeItem(SAVED_HIDDEN_COLS + getActiveProject().getPid())
    localStorage.removeItem(SAVED_COL_ORDER + getActiveProject().getPid())
    sessionStorage.removeItem(SAVED_FLT_KEY + getActiveProject().getPid())
    sessionStorage.removeItem(SAVED_SORT_BY_COL + getActiveProject().getPid())
    sessionStorage.removeItem(SAVED_PAGE_INDEX + getActiveProject().getPid())
    sessionStorage.removeItem(SAVED_PAGE_SIZE + getActiveProject().getPid())
    widthSaved = undefined
    prosConnect.prosLog("clearToDefaultTableSettings")
}

export function resetPageNumber(projectId) {
    sessionStorage.removeItem(SAVED_PAGE_INDEX+projectId)
}

//Identical to PermissionTable
function DefaultColumnFilter({column: {filterValue, preFilteredRows, setFilter}}) {
    //const count = preFilteredRows.length
    return <TimerTextInput
        placeholder={"Suodata..."}
        value={filterValue || ''}
        timerCallback={(val)=> {
            setFilter(val || undefined)
        }}
        size={"sm"}
    />
}

function renderOneDatePicker(title, filterValue, setFilter, ind) {
    return <DatePicker
        label={title}
        value={millisecToDatePicker(filterValue[ind])}
        onChange={(evt)=>{
            const value = datePickerReturnValueToMillisec(evt.target.value)
            if (ind) {
                !filterValue[0] && !value ?
                    setFilter(undefined) :
                    setFilter((old = []) => [old[0], value ? value : NO_DATE_STR])
            } else {
                !filterValue[1] && !value ?
                    setFilter(undefined) :
                    setFilter((old = []) => [value ? value : NO_DATE_STR, old[1]])
            }
        }}
        open={true}
        className={"width-100"}
    />
}
export function DateFilter({column: {filterValue = [], setFilter, preFilteredRows, id}}) {
    const [dropdownOpen, toggleDropdown] = React.useState(gloShowTableFilters)
    useEffect(() => {
        toggleDropdown(gloShowTableFilters)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gloShowTableFilters])

    let timeRangeStr = ''
    if (filterValue.length === 2) {
        let after = ''
        let before = ''
        if (filterValue[1] === NO_DATE_STR && filterValue[2] === NO_DATE_STR) {
            timeRangeStr = "(Puuttuu)"
        } else {
            after = timeFilterToString(filterValue[0])
            before = timeFilterToString(filterValue[1])
            timeRangeStr = after + ' – ' + before
        }
    }

    return <div className={"dropdown-container"}>
        <div className={"element-set-sm dropdown-toggle" + (dropdownOpen ? " open" : "")}
             onClick={() => {
                 if(gloShowTableFilters) return
                 toggleDropdown(!dropdownOpen)
             }}
        >{filterValue.length === 2 ? timeRangeStr : "Valitse"}</div>
        <div className={"date-picker" + (dropdownOpen ? " open" : "")}>
            <label className={"label-set-sm"}>
                <input type="checkbox" id={"tag_none_" + id} value=""
                    onChange={() => {
                    filterValue.length === 2 && filterValue[0] === NO_DATE_STR && filterValue[1] === NO_DATE_STR ?
                        setFilter(undefined) : setFilter([NO_DATE_STR, NO_DATE_STR])
                    }}
                    checked={filterValue.length === 2 && filterValue[0] === NO_DATE_STR && filterValue[1] === NO_DATE_STR}
                />
                <span>(Puuttuu)</span>
            </label>
            {renderOneDatePicker('Alkaen', filterValue, setFilter, 0)}
            {renderOneDatePicker('Päättyen', filterValue, setFilter, 1)}
            <BasicButton
                btnClass={"2nd"}
                label={"Tyhjennä"}
                action={() => setFilter(undefined) }
                disabled={filterValue.length !== 2}
                size={"sm"}
            />
        </div>
    </div>
}

function translateUiTimeResolutionLabel(timeResolution) {
    switch(timeResolution) {
        case FLT_RANGE_NONE: return "Ei suodatusta"
        case FLT_RANGE_MISSING: return "Puuttuu"
        case FLT_RANGE_MONTH: return "Kuukausi"
        case FLT_RANGE_WEEK: return "Viikko"
        case FLT_RANGE_DAY:
        default:
            return "Päivä"
    }
}

function translateUiMandatoryMissingLabel(selection) {
    switch(selection) {
        case FLT_NONE: return "Ei suodatusta"
        case FLT_NO_MISSING: return "Ei puutteita"
        case FLT_MISSING: return "Puutteita"
        default:
            return "Ei suodatusta"
    }
}

export function relativeTimeFilterRange(filterValue) {
    let timeRangeStr = ""
    let timeResolution = FLT_RANGE_NONE
    let min = ""
    let max = ""
    if (filterValue.length === 3) {
        // resolution, min max
        timeResolution = filterValue[0]
        min = filterValue[1]
        max = filterValue[2]
        if (timeResolution === FLT_RANGE_NONE && (min !== '' || max !== '')) {
            timeResolution = FLT_RANGE_DAY
        }
        switch (timeResolution) {
            case FLT_RANGE_NONE: timeRangeStr = '-'; break
            case FLT_RANGE_MISSING: timeRangeStr = "(Puuttuu)"; break
            default:
                timeRangeStr = translateUiTimeResolutionLabel(timeResolution) + " " + min + " ... " + max
        }
    }
    return {
        min: min,
        max: max,
        timeResolution: timeResolution,
        timeRangeStr: timeRangeStr
    }
}

export function RemainingTimeNumPicker({column: {filterValue = [], setFilter, preFilteredRows, id}}) {
    /* Dialog to set remaining time filter.
       Time filter is stored into filter "value" - in this function filterValue as an array [<timeRange>, <min>, <max>]
       Options for time remaining
           FLT_RANGE_NONE No filter - set when press "Clear": column filter is removed by setting as undefined
           FLT_RANGE_MISSING - to get for OBS that do not have expiration time + state is not done:
                            timeRange = "missing", min = max = ""
           FLT_RANGE_DAY, FLT_RANGE_WEEK, FLT_RANGE_MONTH:
                min/after and max/before time limits have two values. Value meaning:
                    1: - this day/week/month
                    2: - this and next day/week/month etc up to 999
                    -1: previous day/week/month etc down to -999
                "" or "-" means the range is not in use i.e. does not limit
    */
    const [dropdownOpen, toggleDropdown] = React.useState(gloShowTableFilters)
    useEffect(() => {
        toggleDropdown(gloShowTableFilters)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gloShowTableFilters])

    let range = relativeTimeFilterRange(filterValue)
    let timeRangeStr = range.timeRangeStr
    let timeResolution = range.timeResolution
    let min = range.min
    let max = range.max

    function changeTimeRange(value, ind){
        if (ind === 1) {
            min =  value
        } else {
            max = value
        }
        if (timeResolution === FLT_RANGE_NONE && (min !== '' || max !== '')) {
            timeResolution = FLT_RANGE_DAY
        }
        setFilter([timeResolution, min, max])
    }

    return <div className={"dropdown-container"}>
        <div className={"dropdown-toggle" + (dropdownOpen ? " open" : "")}
             onClick={() => {
                 if(gloShowTableFilters) return
                 toggleDropdown(!dropdownOpen)
             }}
        >{filterValue.length === 3 ? timeRangeStr : "Valitse"}</div>
        <div className={"date-picker" + (gloShowTableFilters || dropdownOpen ? " open" : "")}>
            <div className={"selection-group selection-group-sm selection-group-radio"}>
                {[FLT_RANGE_NONE, FLT_RANGE_MISSING, FLT_RANGE_MONTH, FLT_RANGE_WEEK, FLT_RANGE_DAY]
                    .map(option => (
                    <RadioButton
                        key={option}
                        checked={timeResolution === option}
                        text={translateUiTimeResolutionLabel(option)}
                        onChange={() => {
                            if (option === FLT_RANGE_NONE) {
                                setFilter(undefined)
                                return
                            } else if (option === FLT_RANGE_MISSING) {
                                min = ""; max = ""
                            } else if (timeResolution === FLT_RANGE_NONE || timeResolution === FLT_RANGE_MISSING) {
                                min = ""; max = 1
                            }
                            setFilter([option, min, max])
                        }}
                    />
                ))}
            </div>
            <div className={"display-flex flex-wrap flex-gap-sm"}>
                <NumPicker text={'Min'}
                           value={min}
                           min={-999} max={999}
                           onChange= {value => changeTimeRange(value, 1)}/>
                <NumPicker text={'Max'}
                           value={max}
                           min={-999} max={999}
                           onChange= {value => changeTimeRange(value, 2)}/>
            </div>
        </div>
    </div>
}

export function MandatoryMissingNumPicker({column: {filterValue = [], setFilter, preFilteredRows, id}}) {
    /* Dialog to set mandatory missing filter.
       Time filter is stored into filter "value" - in this function filterValue as an array [<timeRange>, <min>, <max>]
       Options for time remaining
           FLT_NONE No filter - set when press "Clear": column filter is removed by setting as undefined
           FLT_MISSING - to get for OBS that do have some mandatory values missing
                min = 1 max = ""
           FLT_NO_MISSING - to get for OBS that have no mandatory value missing
                min = "" max = 0
    */
    const [dropdownOpen, toggleDropdown] = React.useState(gloShowTableFilters)
    useEffect(() => {
        toggleDropdown(gloShowTableFilters)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gloShowTableFilters])

    const [min, setMin] = React.useState(filterValue[0] || '')
    const [max, setMax] = React.useState(filterValue[1] || '')
    const [option, setOption] = React.useState(() => {
        if ( ! filterValue) {
            return FLT_NONE
        } else if (min === '' && max === '0') {
            return FLT_NO_MISSING
        } else if (min === '1' && max === '') {
            return FLT_MISSING
        }
        return STR_NO_VALUE
    })

    function changeFilter(filter, opt = STR_NO_VALUE) {
        setMin(filter ? filter[0] : '')
        setMax(filter ? filter[1] : '')
        setFilter(filter)
        setOption(opt)
    }

    return <div className={"dropdown-container"}>
        <div className={"dropdown-toggle" + (dropdownOpen ? " open" : "")}
             onClick={() => {
                 if(gloShowTableFilters) return
                 toggleDropdown(!dropdownOpen)
             }}
        >{filterValue.length === 3 ? 'timeRangeStr' : "Valitse"}</div>
        <div className={"date-picker" + (gloShowTableFilters || dropdownOpen ? " open" : "")}>
            <div className={"selection-group selection-group-sm selection-group-radio"}>
                {[FLT_NONE, FLT_NO_MISSING, FLT_MISSING]
                    .map(opt => (
                        <RadioButton
                            key={opt}
                            checked={option === opt}
                            text={translateUiMandatoryMissingLabel(opt)}
                            onChange={() => {
                                let filter = undefined
                                if (opt === FLT_NO_MISSING) {
                                    filter = ['', '0']
                                } else if (opt === FLT_MISSING) {
                                    filter = ['1', '']
                                }
                                changeFilter(filter)
                                setOption(opt)
                            }}
                        />
                    ))
                }

            </div>
            <div className={"grid-container grid-2"}>
                <NumPicker text={'Min'}
                           value={min}
                           min={0} max={999}
                           onChange= {value => changeFilter([value, max])}/>
                <NumPicker text={'Max'}
                           value={max}
                           min={0} max={999}
                           onChange= {value => changeFilter([min, value])}/>
            </div>
        </div>
    </div>
}

export function CheckDropdown({column: {filterValue, setFilter, preFilteredRows, id}}) {
    const [dropdownOpen, toggleDropdown] = React.useState(gloShowTableFilters)
    useEffect(() => {
        toggleDropdown(gloShowTableFilters)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gloShowTableFilters])

    function filterText() {
        if (filterValue === "include") {
            return prosConnect.getActiveProject().getChTagLabelById(id)
        }
        if (filterValue === "exclude") {
            return "(Puuttuu)"
        }
        return "Valitse"
    }
    const noFilter = !filterValue
    return <div className={"dropdown-container"}>
        <div className={dropdownOpen ? "dropdown-toggle open" : "dropdown-toggle"}
             onClick={() => {
                 if(gloShowTableFilters) return
                 toggleDropdown(!dropdownOpen)
             }}
        >{filterText()}</div>
        <div className={gloShowTableFilters || dropdownOpen ? "checkboxes open" : "checkboxes"}>
            <label className={"label-set-sm" + (noFilter ? " filter-all" : "")}>
                <input type="checkbox" id={"tag_all_" + id} value=""
                    onChange={() => {
                        setFilter(undefined)
                    }}
                    checked={noFilter}
                />
                <span>(Kaikki)</span>
            </label>
            <label className={"label-set-sm"}>
                <input type="checkbox" id={"tag_include_" + id} value=""
                    onChange={() => {
                       setFilter(CH_TAG_INCLUDE)
                    }}
                    checked={filterValue === CH_TAG_INCLUDE || noFilter}
                />
                <span>{prosConnect.getActiveProject().getChTagLabelById(id)}</span>
            </label>
            <label className={"label-set-sm filter-missing"}>
                <input type="checkbox" id={"tag_exclude_" + id} value=""
                    onChange={() => {
                        setFilter(CH_TAG_EXCLUDE)
                    }}
                    checked={filterValue === CH_TAG_EXCLUDE || noFilter}
                />
                <span>(Puuttuu)</span>
            </label>
        </div>
    </div>
}

export function OptionDropdown({column: {filterValue = [], setFilter, preFilteredRows, id}}) {
    const [dropdownOpen, toggleDropdown] = React.useState(gloShowTableFilters)
    useEffect(() => {
        toggleDropdown(gloShowTableFilters)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gloShowTableFilters])

    const setCheckedValues = (checkbox) => {
        let newFilter = filterValue;
        if (!newFilter.includes(checkbox.target.value)) {
            newFilter.push(checkbox.target.value);
        } else {
            newFilter.splice(newFilter.indexOf(checkbox.target.value), 1);
        }
        setFilter(newFilter.length > 0 ? newFilter : undefined);
    }

    const tagInd = prosConnect.getActiveProject() ? prosConnect.getActiveProject().tagIdToIndex(id) : 'missing'
    if (tagInd === 'missing') {
        return <div className={"dropdown-toggle disabled"}>
            Valitse
        </div>
    }
    const activeProject = prosConnect.getActiveProject()
    const optionLabels = activeProject.getRbTagLabelsSorted(tagInd)
    const noFilter = filterValue.length < 1
    return <div className={"dropdown-container"}>
            <div className={dropdownOpen ? "dropdown-toggle open" : "dropdown-toggle"}
                onClick={() => {
                    if(gloShowTableFilters) return
                    toggleDropdown(!dropdownOpen)
                }}
                >{filterValue.length > 0 ? filterValue.join(', ')
                    .replace('missing_tag_value', '(Puuttuu)') : "Valitse"}
            </div>
            <div className={gloShowTableFilters || dropdownOpen ? "checkboxes open" : "checkboxes"}>
            <label className={"label-set-sm"  + (noFilter ? " filter-all" : "")}>
                <input type="checkbox" id={"all-obs"+id} value=""
                    onChange={()=> {
                        if(filterValue.length < 1) setFilter(optionLabels)
                        else setFilter(undefined)
                    }}
                    checked={noFilter}
                />
                <span>(Kaikki)</span>
            </label>
            {optionLabels.map((option, i) => {
                return <label key={"container_"+option+i} className={"label-set-sm"}>
                    <input type="checkbox" id={option} value={option}
                        onChange={e => {setCheckedValues(e)}}
                        checked={filterValue.includes(option) || noFilter}
                    />
                    <span>{option}</span>
                </label>
            })}
            <label className={"label-set-sm filter-missing"}>
                <input type="checkbox" id={"none-obs"+id} value={"missing_tag_value"}
                   onChange={(e) => {setCheckedValues(e)}}
                   checked={filterValue.includes('missing_tag_value') || noFilter}
                />
                <span>(Puuttuu)</span>
            </label>
        </div>
    </div>
}

function getTimeLimit(filterValue, defVal) {
    return filterValue === NO_DATE_STR || !filterValue ? defVal : filterValue
}

export function timeFilterToString(filterValue) {
    return filterValue === NO_DATE_STR ||  !filterValue ? "" : formatDate(filterValue)
}

function getTimeRemaining(timeRange, filterValue, defVal, extraOffset) {
    if (filterValue === "" || filterValue === "-") {
        // no range value use default that means "no limit"
        return defVal
    }
    let offset = prosConnect.getValidInteger(filterValue, defVal, -999, 999)
    if (offset > -999 && offset < 999) {
        offset += extraOffset
    }
    let refTime = new Date()
    if (timeRange === FLT_RANGE_MONTH) {
        // Default ref when offset is 1 is next month 1st day
        refTime.setDate(1)
        refTime.setHours(0, 0, 0)
        refTime.setMonth(refTime.getMonth() + offset)
    } else if (timeRange === FLT_RANGE_WEEK) {
        // Default ref when offset is 1 is next week 1st day
        refTime = getThisWeekMonday(refTime, 0)
        refTime.setDate(refTime.getDate() + offset*7)
    } else { // Default days
        refTime.setHours(0, 0, 0)
        refTime.setDate(refTime.getDate() + offset)
    }
    // console.log("getTimeRemaining() timeRange:", timeRange,
    //    "filterValue:", filterValue, "defVal:", defVal, "->", refTime.toString())
    return refTime.getTime()
}



export const ProjectReportTable = ({ columns, data, selectedRow, activeProjectId, selectedRowState,
                                       columnOrderFromParent, hiddenColumnsFromParent, showComments,
                                       markCopyFromRowIndex, markCopyToRowIndex, markCopyColumnId,
                                       onPageChanged, filterOrSortChanged, renderToolbar,
                                       showColumnFilters, useAutoColor
                                   }) => {
    const tableContent = useRef(null);
    const tableHeader = useRef(null);
    let isSyncingHeaderScroll = false;
    let isSyncingContentScroll = false;
    gloShowTableFilters = showColumnFilters

    useEffect(() => {
        gloShowTableFilters = showColumnFilters
    }, [showColumnFilters])

    const setTableScrollPosition = (e) => {
        if (e.target === tableHeader.current) {
            if (!isSyncingHeaderScroll) {
                window.requestAnimationFrame(() => {
                    isSyncingContentScroll = true;
                    tableContent.current.scrollLeft = tableHeader.current.scrollLeft;
                })
            }
            isSyncingHeaderScroll = false;
        }
        if (e.target === tableContent.current) {
            if (!isSyncingContentScroll) {
                window.requestAnimationFrame(() => {
                    isSyncingHeaderScroll = true;
                    tableHeader.current.scrollLeft = tableContent.current.scrollLeft;
                })
            }
            isSyncingContentScroll = false;
        }
    }

    const setCellBackgroundColor = (row, tagId) => {
        if (row && row.original) {
            let hexNumber = prosConnect.getActiveProject().getBgHexNumber(row.original, tagId, useAutoColor);
            if (hexNumber) {
                return {backgroundColor: '#' + hexNumber}
            }
        }
        return null
    }

    const sortTypes = React.useMemo(() => ({
        sortObs: (rowA, rowB, id, desc) => {
            let a = rowA.original.ui_origRow
            let b = rowB.original.ui_origRow
            return a-b
        },
        tagSortTxt: (rowA, rowB, id, desc) => {
            let a = prosConnect.getObjAttr(rowA.original, id)
            let b = prosConnect.getObjAttr(rowB.original, id)
            return a.localeCompare(b)
        },
        tagSortRb: (rowA, rowB, id, desc) => {
            let a = prosConnect.getObjIntAttr(rowA.original, id)
            let b = prosConnect.getObjIntAttr(rowB.original, id)
            return a-b
        },
        sortTime: (rowA, rowB, id, desc) => {
            let a = prosConnect.getObjIntAttr(rowA.original, id)
            let b = prosConnect.getObjIntAttr(rowB.original, id)
            return a-b
        },
        sortMandatory: (rowA, rowB, id, desc) => {
            let a = prosConnect.getActiveProject().countMissingMandatoryAttribute(rowA.original)
            let b = prosConnect.getActiveProject().countMissingMandatoryAttribute(rowB.original)
            return a-b
        },
    }), [])

    const filterTypes = React.useMemo(() => ({
            // text filter prosMultiFilterText() is
            // - normal type of 'contains' / inclusive
            // - with "-" minus on front exclusive
            // - comma delimeted for multiple words
            text: (rows, id, filterValue) => {
                return rows.filter(row => {
                    const rowValue = row.values[id]
                    return rowValue !== undefined
                        ? prosMultiFilterText(String(filterValue), String(rowValue))
                        : true
                })
            },
            uiTextAndComments: (rows, id, filterValue) => {
                if (!filterValue) {
                    return true
                }
                return rows.filter(row => {
                    const obsText = row.original.ui_text ? row.original.ui_text : ''
                    const obsCommentsText = showComments ? row.original.obsObjList
                        .filter(itm => itm.obj_type === OBJ_TYPE_ITM ||
                                        itm.obj_type === OBJ_TYPE_TXT_SOUND)
                        .map(itm => prosConnect.getItemText(itm))
                        .toString() : ''
                    return prosMultiFilterText(String(filterValue), String(obsText + ',' + obsCommentsText))
                })
            },
            includesAndEmpty: (rows, ids, filterValue) => {
                if (ids.length < 1 || !prosConnect.getActiveProject()) return true
                let tag = ids[0]
                let tagIndex = prosConnect.getActiveProject().tagIdToIndex(tag)
                let tagLabels = prosConnect.getActiveProject().getRbTagLabels(tagIndex)
                let filterTagInds = filterValue.map(label => tagLabels.indexOf(label).toString())
                return rows.filter(row => {
                    return ids.some(id => {
                        const rowValue = row.original[id]
                        if (typeof rowValue === 'undefined' || rowValue === STR_NO_VALUE) {
                            return filterValue.includes('missing_tag_value')
                        }
                        return filterTagInds.includes(rowValue)
                    })
                })
            },
            checkTags: (rows, id, filterValue) => {
                return rows.filter(row => {
                    const rowValue = row.original[id];
                    if (filterValue === "include") {
                        return rowValue === "Y"
                    }
                    if (filterValue === "exclude") {
                        return rowValue === "n" || rowValue === undefined
                    }
                    return true
                })
            },
            betweenDates: (rows, id, filterValue) => {
                let validFilter = filterValue.length === 2
                let filterDateMissing = validFilter &&
                    filterValue[0] === NO_DATE_STR &&
                    filterValue[1] === NO_DATE_STR
                let afterMs = getTimeLimit(filterValue[0], MIN_EXPIRE_TIME_MS)
                let beforeMs = getTimeLimit(filterValue[1], Number.MAX_SAFE_INTEGER)
                return rows.filter(row => {
                    //todo optimize by checking expire time range and that is integer when load from server
                    let timeValMs = row.original[id] ? parseInt(row.original[id]) : 0
                    if (timeValMs < MIN_EXPIRE_TIME_MS) {
                        timeValMs = 0
                    }
                    if (filterDateMissing) {
                        return !timeValMs
                    }
                    return timeValMs >= afterMs && timeValMs <= beforeMs
                })
            },
            betweenNumbers: (rows, id, filterValue) => {
                let validFilter = filterValue.length === 3
                const timeRange = filterValue[0]
                let minVal = filterValue[1]
                let maxVal = filterValue[2]
                let missingFilterValues = timeRange === FLT_RANGE_MISSING
                const afterMs = getTimeRemaining(timeRange, minVal, 0, 0)
                const beforeMs = getTimeRemaining(timeRange, maxVal, Number.MAX_SAFE_INTEGER, 1)
                return rows.filter(row => {
                    const expireTimeMs = row.original[id]
                    if (!validFilter) {
                        return true
                    }
                    if (missingFilterValues) {
                        return !expireTimeMs
                    }
                    if (!expireTimeMs) {
                        // OBS is not ongoing or does not have expiration date - ref setting ui_timeToDate (id)
                        return missingFilterValues
                    }
                    return expireTimeMs >= afterMs && expireTimeMs <= beforeMs
                })
            },
            betweenNumbersMandatory: (rows, id, filterValue) => {
                let validFilter = filterValue.length === 2
                if ( ! validFilter) {
                    return rows
                }
                const minVal = getValidInteger(filterValue[0], 0)
                const maxVal = getValidInteger(filterValue[1], Number.MAX_SAFE_INTEGER)
                return rows.filter(row => {
                    const missingMandatory = prosConnect.getActiveProject().countMissingMandatoryAttribute(row.original)
                    return missingMandatory >= minVal && missingMandatory <= maxVal
                })
            }
        }),
        [showComments]
    )

    const defaultColumn = React.useMemo(() => ({
        Filter: DefaultColumnFilter,
        minWidth: 50,
        width: PROS_TBL_DEFAULT_WIDTH,
    }),[])

    const {
        getTableBodyProps,
        headerGroups,
        rows,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        prepareRow,
        preGlobalFilteredRows,
        setColumnOrder,
        setHiddenColumns,
        state,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data,
            defaultColumn, // Be sure to pass the defaultColumn option
            filterTypes,
            sortTypes,
            autoResetFilters: false,
            initialState: restoreTableSettings(activeProjectId, 'intialState', false)
        },
        useGlobalFilter,
        useFilters,
        useSortBy,
        usePagination,
        useResizeColumns,
        useFlexLayout,
        useColumnOrder,
    )

    React.useEffect(() => {
        // prosLogX("TABLE: +MOUNT+", activeProjectId, "columns:", columns.length)
        shareRows = rows
        // if (columns.length > 7) { prosLogX("Column6-7:",
        //        columns[6].id, columns[6].Header, columns[6].width,
        //        columns[7].id, columns[7].Header, columns[7].width)
        return function cleanup() {
            // prosLogX("TABLE: -UNMOUNT-", activeProjectId)
            shareRows = undefined
        };
    }, [rows])    // [activeProjectId, columns, rows])

    React.useEffect(() => {
        setTableFilter(activeProjectId, state.filters)
        // Fix #23.202 - reset page number when changing filters, next fix: #23.432
        if (state.pageIndex >= pageCount) {
            gotoPage(0)
        }
        filterOrSortChanged('filters', state.filters)
    }, [state.filters, state.pageIndex, pageCount, activeProjectId, filterOrSortChanged, gotoPage])

    React.useEffect(() => {
        setTableSort(activeProjectId, state.sortBy)
        filterOrSortChanged('sortBy', state.sortBy)
    }, [state.sortBy, activeProjectId, filterOrSortChanged])

    React.useEffect(() => {
        sessionStorage.setItem(SAVED_PAGE_SIZE + activeProjectId, JSON.stringify(state.pageSize))
    }, [state.pageSize, activeProjectId])

    React.useEffect(() => {
        sessionStorage.setItem(SAVED_PAGE_INDEX + activeProjectId, JSON.stringify(state.pageIndex))
    }, [state.pageIndex, activeProjectId])

    React.useEffect(() => {
        if (hiddenColumnsFromParent) {
            setHiddenColumns(hiddenColumnsFromParent)
        }
    }, [hiddenColumnsFromParent, setHiddenColumns])

    React.useEffect(() => {
        // prosLogX("TABLE: columnOrderFromParent:", columnOrderFromParent)
        setColumnOrder(columnOrderFromParent)
    }, [columnOrderFromParent, setColumnOrder])

    React.useEffect(() => {
        exposeActiveColumnWidths(state.columnResizing.columnWidths)
    }, [state.columnResizing])

    const allObs = preGlobalFilteredRows
    const obsStateCount = []
    const tagIdForState = prosConnect.getActiveProject().getStateRbTag()
    if (tagIdForState) {
        allObs.forEach(function(value) {
            obsStateCount[value.original[tagIdForState]] = (obsStateCount[value.original[tagIdForState]] || 0) + 1;
        })
    }

    // To expose filtered OBS rows for LocationPictureList - make list in obs loop below
    filteredObsIds.length = 0
    filteredObsChangeCount++
    const filteredObs = rows;
    shareRows = rows
    const filteredStateCount = [];
    filteredObs.forEach(function(value) {
        filteredStateCount[value.original[tagIdForState]] = (filteredStateCount[value.original[tagIdForState]] || 0) + 1;
        filteredObsIds.push(value.original.obj_id)
    })
    //get all others after info state
    const otherObsStatecount = obsStateCount.slice(5)
    const otherFilteredStateCount = filteredStateCount.slice(5)
    // Debug - how to show what filter and sort parameters are in use
    // console.log("filters:", state.filters)
    // console.log("sortBy:", state.sortBy)
    let tagNumber = prosConnect.getActiveProject().tagIdToIndex(tagIdForState)
    function RenderTableStats() {
        const activeProject = prosConnect.getActiveProject()
            if (obsStateCount.length) {
                let mvTag = activeProject.getMvTagSettings(tagNumber)
                if (!mvTag) {
                    // Protected if subprojects state MV option does not have options
                    return null
                }
                return <div className={"table-stats"}>
                    <div className={"element-set-sm"}>
                    {obsStateCount.slice(0, 5).map((value, index) => {
                        let option = mvTag.options.find(o => o.optionIndex === index)
                        if (!option || !option.inUse) { return null }
                        return  <span key={index}
                                data-tooltip={option.label}
                                className={"icon-text-set tooltip-bottom"}
                            >
                                <i
                                    className={"ui-icon ui-icon-md " + getIconClass(index)}
                                />
                                {state.filters.length > 0 ?
                                        <span><b>{filteredStateCount[index] || "–"}</b>/{value}</span> :
                                        <span><b>{value}</b></span>}
                            </span>
                    })}
                    {otherObsStatecount.length > 0 ? <span
                            data-tooltip={["Muut"]}
                            className={"tooltip-bottom"}
                        >
                            <i
                                className={"ui-icon ui-icon-md " + getIconClass(5)}//get "other" icon
                            />
                                <b><span>{calculateArraySum(otherFilteredStateCount) || "–"}</span>/{calculateArraySum(otherObsStatecount)}</b>
                        </span> : null}
                    {state.filters.length > 0 ?
                            <span>Yht. <b><span>{filteredObs.length || "–"}</span>/{preGlobalFilteredRows.length}</b></span> :
                                <span>Yht. <b>{preGlobalFilteredRows.length}</b></span>
                    }
                    </div>
                    <div className={"totals"}>
                        <span className={"color-main"}>{filteredObs.length || "–"}</span>/<span>{activeProject.itemCountStats.obs}</span>
                    </div>
                </div>
            }
        return null
    }

    function renderTableControls(){
        return  <div className={"table-controls"}>
            <div className="table-options element-set">
                <div className="btn-set-2nd">
                    <DropdownMenu
                        values={[
                            {value:10,text:10},
                            {value:20,text:20},
                            {value:50,text:50},
                            {value:100,text:100},
                            {value:200,text:200}
                        ]}
                        currentValue={pageSize}
                        onSelect={(retVal)=>{
                            setPageSize(retVal)
                        }}
                        btnClass={"3rd"}
                    />
                    <BasicButton
                        action={() => {gotoPage(0); onPageChanged()}}
                        icon={"chevron-left-stop"}
                        iconSize={"md"}
                        disabled={!canPreviousPage}
                     />
                    <BasicButton
                        action={() => { previousPage(); onPageChanged()}}
                        icon={"chevron-left"}
                        iconSize={"md"}
                        disabled={!canPreviousPage}
                    />
                    <span className={!pageOptions.length ? "disabled" : ""}>
                        <div>{pageOptions.length ? pageIndex + 1 + "/" + pageOptions.length : ""} </div>
                    </span>
                    <BasicButton
                        action={() => { nextPage(); onPageChanged()}}
                        icon={"chevron-right"}
                        iconSize={"md"}
                        disabled={!canNextPage}
                    />
                    <BasicButton
                        action={() => {gotoPage(pageCount - 1); onPageChanged()}}
                        icon={"chevron-right-stop"}
                        iconSize={"md"}
                        disabled={!canNextPage}
                    />
                </div>
            </div>
            <div className={"element-set dom-hide dom-show_lg"}>
                <RenderTableStats />
            </div>
            {renderToolbar()}
        </div>
    }

    function renderTableListing(){
        if(!rows.length && headerGroups.length > 0) return <tr>
            {headerGroups[0].headers.map(column=>{
                return <td key={'td__' + column.id}
                    {...column.getHeaderProps([{className: column.className}])}
                ><p style={{minHeight: 'var(--element-size)'}}/></td>
            })}
        </tr>

        return  page.map((row, i) => {
                prepareRow(row)
                return <tr {...row.getRowProps([{}])}
                        className={row.original.obj_id === selectedRowState ? "active" : ""}
                    >
                        {row.cells.map(cell => {
                            const cellClass = setCellBackgroundColor(row, cell.column.id) ? " bgcolor-hilite" : ""
                            if (markCopyFromRowIndex < 0) {
                                // Normal - not copy source or destination
                                return <td
                                    onClick={()=> {
                                       if(cell.column.id.indexOf(TAG_CH_PREFIX) !== 0) selectedRow(row, cell)
                                    }}
                                    className={(cell.column.className || "") + cellClass}
                                    {...cell.getCellProps([{
                                        style: setCellBackgroundColor(row, cell.column.id) || {}}])}
                                >
                                    {cell.render('Cell')}
                                </td>
                            }
                            let isDragCopyColumn = cell.column.id === markCopyColumnId
                            let rowInd = pageIndex * pageSize + i
                            if (!isDragCopyColumn) {
                                // Table cell - dragging cell copy mode - cell's for other than a selected column
                                return <td
                                    className= {"what " + cell.column.className + cellClass}
                                    {...cell.getCellProps([{
                                        style: setCellBackgroundColor(row, cell.column.id)}])}>
                                    {cell.render('Cell')}
                                </td>
                            }
                            if (isDragCopyColumn && rowInd === markCopyFromRowIndex) {
                                // Table cell - dragging cell copy mode - source cell
                                return <td
                                    className= {"drag-start " + cell.column.className + cellClass}
                                    {...cell.getCellProps([{
                                        style: setCellBackgroundColor(row, cell.column.id)}])}
                                >
                                    {cell.render('Cell')}
                                </td>
                            }
                            if (isDragCopyColumn && markCopyToRowIndex >= 0) {
                                // When rendering row is between src and dst rows indicate as target for copy
                                let rowInDestinationRange =
                                    (markCopyToRowIndex > markCopyFromRowIndex &&
                                        rowInd > markCopyFromRowIndex && rowInd <= markCopyToRowIndex) ||
                                    (markCopyToRowIndex < markCopyFromRowIndex &&
                                        rowInd < markCopyFromRowIndex && rowInd >= markCopyToRowIndex)
                                if (rowInDestinationRange)
                                    // Table cell - dragging cell copy mode - destination cell
                                    return <td
                                        className= {"dragover " + cell.column.className + cellClass}
                                        {...cell.getCellProps([{
                                            style: setCellBackgroundColor(row, cell.column.id)}])}
                                    >
                                        {cell.render('Cell')}
                                    </td>
                            }
                            return <td
                                className= {cell.column.className + cellClass}
                                {...cell.getCellProps([{
                                    style: setCellBackgroundColor(row, cell.column.id)}])}
                            >
                                {cell.render('Cell')}
                            </td>
                        })}
                    </tr>
            })
    }

    function renderTableResults(tableContent){
        return <tbody {...getTableBodyProps()}
                onScroll={setTableScrollPosition}
                ref={tableContent}
            >
            {renderTableListing()}
        </tbody>
    }

    // share page size for parent
    sharePageSize = pageSize
    return <>
        <div className={"table-container"}>
            {renderTableControls()}
            <table role={"table"}>
                <thead ref={tableHeader} onScroll={setTableScrollPosition}>
                {headerGroups.map(headerGroup => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map(column => (
                            <th {...column.getHeaderProps([{className: (column.className ? column.className : "") + (column.filterValue?.length > 0 ? " filters" : "")}])}>
                                <div className={"element-set-sm header-text"
                                        + (column.isSorted ? " sorted" : "")
                                    }
                                     {...column.getSortByToggleProps()} title={"Lajittele"}
                                     data-content={column.render('Header')}
                                >
                                    <div className={"th-text"}>{column.render('Header')}</div>
                                    {column.isSorted ? column.isSortedDesc ?
                                        <BasicButton
                                            icon={"sort-down"}
                                            size={"xs"}
                                            title={"lajittele"}
                                            hilite={true}
                                            selected={true}
                                        /> :
                                        <BasicButton
                                            icon={"sort-up"}
                                            size={"xs"}
                                            title={"lajittele"}
                                            hilite={true}
                                            selected={true}
                                        />  :
                                        <BasicButton
                                            icon={"sort"}
                                            size={"xs"}
                                            title={"lajittele"}
                                        />}
                                </div>
                                    {column.canFilter ? column.render('Filter') : null}
                                <div className={"resizer"}
                                     {...column.getResizerProps()}>
                                </div>
                            </th>
                            )
                        )}
                    </tr>
                ))}
                </thead>
                {renderTableResults(tableContent)}
            </table>
        </div>
        {!rows.length && state.filters.length > 0 ?
            <div className={"msg msg-info"}>
                <p className={"text-center"}>Ei tuloksia valituilla suodatuksilla</p>
            </div>
            : rows.length ? "" :
                <div className={"msg msg-info"}>
                    <p className={"text-center"}>Aloita valitsemalla raportti</p>
                </div>}

    </>
}


